import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { Col, Container, Card, CardImg, CardBody, CardTitle, CardText, Row, Button, CardFooter } from 'reactstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons/faCaretRight';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faSignalStream } from '@fortawesome/pro-solid-svg-icons/faSignalStream';

const CarouselV3 = ({
  cards = [],
  darkText = false,
  fullWidth = false,
  handleNav,
  description,
  noDescription = false,
  noMetadata = false,
  noSubtitle = false,
  linkTo,
  title,
  sortBy,
  sortOrder = 'desc',
  variant,
  onReplay = [],
  live = [],
  premieresText = 'Premieres',
  className,
  titleTag: TitleTag = 'h5',
  children = null
}) => {
  const containerUlRef = useRef(null);
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(false);

  const onCarouselScroll = useCallback(el => {
    const carousel = el?.currentTarget ?? containerUlRef.current;
    if (!carousel || !carousel.firstChild || !carousel.lastChild) return;
    const { left, right } = carousel.getBoundingClientRect();
    setPrevDisabled(left <= carousel.firstChild.getBoundingClientRect().left);
    setNextDisabled(right >= carousel.lastChild.getBoundingClientRect().right);
  }, []);

  const page = useCallback(
    (dir = 'right') => {
      const el = containerUlRef.current;
      if (!el) return;

      const boundingBox = el.getBoundingClientRect();
      const bounds = dir === 'right' ? boundingBox.right : boundingBox.left;

      const nextEl = Array.from(el.children).find(c =>
        dir === 'right' ? c.getBoundingClientRect().right > bounds : c.getBoundingClientRect().left < bounds
      );

      if (nextEl) {
        nextEl.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    },
    [containerUlRef]
  );

  const prevPage = useCallback(() => page('left'), [page]);
  const nextPage = useCallback(() => page('right'), [page]);

  useEffect(() => {
    onCarouselScroll();
  });

  // A handy utility function to access nested properties safely
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  // Sort the cards array if 'sortBy' exists, respecting the desired 'sortOrder' (asc/desc)
  let sortedCards = sortBy
    ? [...cards].sort((a, b) => {
        const aValue = getNestedValue(a, sortBy);
        const bValue = getNestedValue(b, sortBy);

        const aDate = aValue ? new Date(aValue) : null;
        const bDate = bValue ? new Date(bValue) : null;

        let comparison = 0;

        if (aDate instanceof Date && !isNaN(aDate) && bDate instanceof Date && !isNaN(bDate)) {
          comparison = aDate - bDate;
        } else if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        }

        return sortOrder === 'desc' ? -comparison : comparison;
      })
    : cards;

  // If any elements of sortedCards is a 'bonus', use the child link/download instead
  sortedCards = sortedCards.map(c => {
    if (c.type === 'bonus' && c.Children && c.Children.length > 0) {
      return c.Children[0];
    }
    return c;
  });

  const header = (
    <TitleTag className={classNames('text-white d-flex align-items-center mr-3', darkText && 'text-dark')}>
      {title}
      {linkTo && (
        <>
          &nbsp; <FontAwesomeIcon icon={faCaretRight} />
        </>
      )}
    </TitleTag>
  );

  return (
    <Container size="2xl" className={classNames('carousel', variant && `carousel-${variant}`, className)}>
      <Row className={fullWidth ? '' : 'px-sm-5'}>
        <Col>
          {linkTo ? (
            <Link to={linkTo} className="text-small text-white">
              {header}
            </Link>
          ) : (
            header
          )}
          {description && <p className={classNames('text-white mb-3', darkText && 'text-dark')}>{description}</p>}
        </Col>
      </Row>
      <Row className={classNames('carouselContainer', fullWidth ? '' : 'px-sm-5')}>
        <ul className="carouselNav d-none d-sm-block">
          <li>
            <Button onClick={prevPage} color="link" disabled={prevDisabled} className="carouselNav-prev">
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
          </li>
          <li>
            <Button onClick={nextPage} color="link" disabled={nextDisabled} className="carouselNav-next">
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </li>
        </ul>
        <Col xs="auto">
          <ul ref={containerUlRef} onScroll={onCarouselScroll}>
            {sortedCards && sortedCards.length
              ? sortedCards.map((card, i) => {
                  const {
                    date = null,
                    duration = null,
                    image = null,
                    subtitle = null,
                    title = null,
                    description = null,
                    icon
                  } = card.displayProps || {};

                  const { id, slug, type, data } = card;

                  let durationObject, dateObject;
                  if (duration) {
                    durationObject = moment.duration(duration);
                  }
                  if (date) {
                    dateObject = new Date(date);
                  }

                  console.log('CARD', type, data);

                  return (
                    <Card
                      onClick={
                        slug
                          ? () =>
                              handleNav(
                                null,
                                type,
                                ['link', 'download'].includes(type)
                                  ? (data?.url ?? slug)
                                  : type === 'zoomMeeting'
                                    ? (data?.recordingUrl ?? slug)
                                    : slug,
                                null
                              )
                          : null
                      }
                      tag="li"
                      style={{ minWidth: 200, cursor: slug ? 'pointer' : 'default' }}
                      key={id}
                    >
                      {image && <CardImg alt={image ? image.alt : ''} src={image ? image.url : ''} top width="100%" />}
                      {icon && <CardImg alt="" top tag={FontAwesomeIcon} icon={icon} size="4x" className="mt-3" />}
                      <CardBody>
                        <CardTitle tag="h6">{title}</CardTitle>
                        {!noSubtitle && subtitle && <CardText className="subtitle">{subtitle}</CardText>}
                        {!noDescription && description && <CardText className="description">{description}</CardText>}
                      </CardBody>
                      {!noMetadata && (duration || date) && (
                        <CardFooter>
                          {live.includes(i) && (
                            <div style={{ color: 'var(--red)', fontWeight: 600 }}>
                              <FontAwesomeIcon icon={faSignalStream} />
                              &nbsp; Live Now!
                            </div>
                          )}
                          {!onReplay.includes(i) && !live.includes(i) && (
                            <>
                              {duration && (
                                <small className="text-muted mr-2">{`${
                                  durationObject.hours() ? `${durationObject.hours()} hrs ` : ''
                                }${durationObject.minutes()} min${durationObject.minutes() > 1 ? 's' : ''}`}</small>
                              )}
                              {!live.includes(id) && date && (
                                <small className="text-muted mr-2">
                                  {dateObject > new Date() && premieresText && <>{premieresText}&nbsp;</>}
                                  {dateObject.toLocaleDateString(undefined, { dateStyle: 'long' })}
                                </small>
                              )}
                            </>
                          )}
                        </CardFooter>
                      )}
                    </Card>
                  );
                })
              : null}
          </ul>
        </Col>
      </Row>
      {children}
    </Container>
  );
};

CarouselV3.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.object),
  darkText: PropTypes.bool,
  fullWidth: PropTypes.bool,
  handleNav: PropTypes.func,
  description: PropTypes.string,
  noDescription: PropTypes.bool,
  noMetadata: PropTypes.bool,
  noSubtitle: PropTypes.bool,
  linkTo: PropTypes.string,
  title: PropTypes.string,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  variant: PropTypes.string,
  onReplay: PropTypes.arrayOf(PropTypes.number),
  live: PropTypes.arrayOf(PropTypes.number),
  premieresText: PropTypes.string
};

CarouselV3.defaultProps = {
  cards: [],
  darkText: false,
  fullWidth: false,
  handleNav: null,
  description: null,
  noDescription: false,
  noMetadata: false,
  noSubtitle: false,
  linkTo: null,
  title: null,
  sortBy: null,
  sortOrder: 'desc',
  variant: null,
  onReplay: [],
  live: [],
  premieresText: 'Premieres'
};

export default CarouselV3;
